// Criar tarefa (P0.1): campo sempre visível no topo, "Hoje" em um clique, horário só
// depois da data, atalhos de estimativa, Enter salva.
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, toDateKey } from '../domain/dates';
import {
  ESTIMATE_SHORTCUTS,
  LIMITS,
  validateTaskInput,
  type TaskError,
  type TaskField,
  type TaskFields,
} from '../domain/task';
import { parseDateKey } from '../domain/dates';
import { formatMinutes } from '../i18n/format';
import { useAppState, useStore } from '../state/AppStore';
import { useNow } from '../state/useNow';
import { Calendar } from './Calendar';
import { Icon } from './Icon';
import { MinutesInput } from './MinutesInput';
import { TimeInput } from './TimeInput';

export interface FieldsState {
  title: string;
  description: string;
  date: string | null;
  time: string;
  estimate: number | null;
}

export const emptyFields = (): FieldsState => ({ title: '', description: '', date: null, time: '', estimate: null });

export function toInput(f: FieldsState) {
  return {
    title: f.title,
    description: f.description,
    due_date: f.date,
    due_time: f.date ? f.time || null : null,
    estimate_minutes: f.estimate,
  };
}

export function cls(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

interface EditorProps {
  value: FieldsState;
  onChange(next: FieldsState): void;
  errors: Partial<Record<TaskField, TaskError>>;
  disabled?: boolean;
  showDescription?: boolean;
  /** no lugar do prazo (Hoje/Amanhã/data/horário): os dias da tarefa recorrente (ASM-110) */
  schedule?: ReactNode;
}

/** Prazo + estimativa (+ descrição): usado ao criar e ao editar. */
export function TaskDetailsEditor({ value, onChange, errors, disabled, showDescription = true, schedule }: EditorProps) {
  const { t, i18n } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dateChip = useRef<HTMLButtonElement>(null);
  /** fecha o calendário e devolve o foco ao chip (Enter segue salvando a tarefa) */
  const closeCalendar = () => {
    setCalendarOpen(false);
    dateChip.current?.focus();
  };
  const now = useNow(60_000);
  const today = toDateKey(new Date(now));
  const tomorrow = addDays(today, 1);
  const dayAfter = addDays(today, 2);
  const [customOpen, setCustomOpen] = useState(
    value.estimate != null && !(ESTIMATE_SHORTCUTS as readonly number[]).includes(value.estimate)
  );
  const set = (patch: Partial<FieldsState>) => onChange({ ...value, ...patch });
  const timeFormat = useAppState((s) => s.profile?.time_format ?? '24h');
  const pickDate = (d: string | null) => set({ date: value.date === d ? null : d, time: d ? value.time : '' });
  const customDate = value.date && ![today, tomorrow, dayAfter].includes(value.date) ? value.date : null;

  return (
    <div className="details">
      {showDescription && (
        <div className="field">
          <textarea
            className="input input--area"
            aria-label={t('newTask.description')}
            placeholder={t('newTask.descriptionPlaceholder')}
            value={value.description}
            disabled={disabled}
            rows={2}
            onChange={(e) => set({ description: e.target.value })}
          />
          <span className={cls('counter', [...value.description].length > LIMITS.description && 'counter--over')}>
            {t('newTask.counter', { n: [...value.description].length, max: LIMITS.description })}
          </span>
          {errors.description && <p className="field-error">{t(`errors.task.${errors.description}`, { max: LIMITS.description })}</p>}
        </div>
      )}

      {schedule ?? (
        <>
          <p className="label-caps">{t('newTask.deadline')}</p>
          <div className="chips">
            <button type="button" className={cls('chip', value.date === today && 'chip--on')} aria-pressed={value.date === today} disabled={disabled} onClick={() => pickDate(today)}>
              <Icon name="calendar" size={13} />
              {t('newTask.today')}
            </button>
            <button type="button" className={cls('chip', value.date === tomorrow && 'chip--on')} aria-pressed={value.date === tomorrow} disabled={disabled} onClick={() => pickDate(tomorrow)}>
              {t('newTask.tomorrow')}
            </button>
            <button type="button" className={cls('chip', value.date === dayAfter && 'chip--on')} aria-pressed={value.date === dayAfter} disabled={disabled} onClick={() => pickDate(dayAfter)}>
              {t('newTask.dayAfterTomorrow')}
            </button>
            {/* "Escolher data" abre o calendário (AC-213); com uma data escolhida, o chip mostra a data */}
            <span
              className="date-pick"
              onKeyDown={(e) => {
                if (e.key !== 'Escape' || !calendarOpen) return;
                // Esc fecha só o calendário (não cancela a tarefa nem fecha o pop-up de edição)
                e.stopPropagation();
                closeCalendar();
              }}
            >
              <button
                ref={dateChip}
                type="button"
                className={cls('chip', customDate && 'chip--on')}
                aria-label={t('newTask.pickDate')}
                aria-expanded={calendarOpen}
                aria-pressed={!!customDate}
                disabled={disabled}
                onClick={() => setCalendarOpen(!calendarOpen)}
              >
                <Icon name="calendar" size={13} />
                <span>{customDate ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(parseDateKey(customDate)) : t('newTask.pickDate')}</span>
              </button>
              {calendarOpen && (
                <Calendar
                  value={value.date}
                  min={today}
                  anchor={dateChip.current}
                  onClose={closeCalendar}
                  onPick={(date) => {
                    closeCalendar();
                    set({ date, time: value.time });
                  }}
                />
              )}
            </span>
            {/* O campo de horário só aparece depois que uma data é escolhida (AC-005). */}
            {value.date && (
              <TimeInput
                className={cls('chip', value.time && 'chip--on')}
                label={t('newTask.time')}
                value={value.time}
                format={timeFormat}
                disabled={disabled}
                onChange={(time) => set({ time })}
              />
            )}
          </div>
          {(errors.due_date || errors.due_time) && <p className="field-error">{t(`errors.task.${errors.due_date ?? errors.due_time}`)}</p>}
        </>
      )}

      <p className="label-caps">{t('newTask.estimate')}</p>
      <div className="chips">
        {ESTIMATE_SHORTCUTS.map((min) => (
          <button
            key={min}
            type="button"
            className={cls('chip', value.estimate === min && !customOpen && 'chip--on')}
            aria-pressed={value.estimate === min && !customOpen}
            disabled={disabled}
            onClick={() => {
              setCustomOpen(false);
              set({ estimate: value.estimate === min ? null : min });
            }}
          >
            {formatMinutes(min, t)}
          </button>
        ))}
        <button
          type="button"
          className={cls('chip', customOpen && 'chip--on')}
          aria-pressed={customOpen}
          disabled={disabled}
          onClick={() => {
            setCustomOpen(!customOpen);
            if (customOpen) set({ estimate: null });
          }}
        >
          {t('newTask.other')}
        </button>
        {customOpen && (
          <MinutesInput
            label={t('newTask.otherEstimate')}
            placeholder={t('newTask.otherPlaceholder')}
            initialMinutes={value.estimate}
            disabled={disabled}
            onChange={(estimate) => set({ estimate })}
          />
        )}
      </div>
      {errors.estimate_minutes && <p className="field-error">{t(`errors.task.${errors.estimate_minutes}`)}</p>}
    </div>
  );
}

export function NewTaskForm({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const now = useNow(60_000);
  const today = toDateKey(new Date(now));
  const [fields, setFields] = useState<FieldsState>(emptyFields);
  const [expanded, setExpanded] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<TaskField, TaskError>>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // modo de adicionar: o campo aberto (escuro, borda verde) em vez do botão amarelo
  const [active, setActive] = useState(false);
  const pressing = useRef(false);

  useEffect(() => {
    const down = () => (pressing.current = true);
    const up = () => (pressing.current = false);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('pointercancel', up, true);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('pointercancel', up, true);
    };
  }, []);

  const deactivate = () => {
    if (formRef.current?.contains(document.activeElement)) return;
    setActive(false);
    // virou o botão amarelo de novo (título vazio, sem "Mais detalhes"): saiu do modo de
    // adicionar, então as escolhas guardadas da última tarefa são limpas (AC-215)
    setFields((f) => (f.title || expanded ? f : emptyFields()));
  };

  /**
   * Saiu do campo. Se foi um clique (ex.: "Iniciar" numa tarefa), o campo só encolhe
   * depois que o botão do mouse solta: encolher no meio do clique sobe a lista e o
   * clique se perde (AC-212).
   */
  const onBlur = (e: FocusEvent<HTMLFormElement>) => {
    if (formRef.current?.contains(e.relatedTarget as Node | null)) return;
    if (!pressing.current) return deactivate();
    window.addEventListener('pointerup', () => setTimeout(deactivate, 0), { once: true });
  };

  /** Esc ou "Cancelar": sai do modo de adicionar e limpa tudo (AC-215). */
  const exit = () => {
    setFields(emptyFields());
    setErrors({});
    setExpanded(false);
    setActive(false);
    (document.activeElement as HTMLElement | null)?.blur();
  };

  const submit = async () => {
    if (disabled) return;
    const v = validateTaskInput(toInput(fields));
    if (!v.ok) {
      // Título vazio não salva (AC-002) — sem mensagem, a lista só continua igual.
      setErrors(fields.title.trim() ? v.errors : { ...v.errors, title: undefined });
      return;
    }
    const value: TaskFields = v.value;
    // o campo segue ativo para a próxima, com prazo, duração e "Mais detalhes" como estavam;
    // título, descrição e o horário do prazo começam vazios (AC-214)
    setFields((f) => ({ ...emptyFields(), date: f.date, estimate: f.estimate }));
    setErrors({});
    await store.createTask(value);
    inputRef.current?.focus();
  };

  // Enter salva — no campo de título, nos outros campos e também com o foco num chip
  // (antes o Enter "clicava" no chip "Hoje" e desmarcava em vez de salvar).
  const onFormKey = (e: KeyboardEvent<HTMLFormElement>) => {
    const el = e.target as HTMLElement;
    if (e.key === 'Escape') {
      exit();
      return;
    }
    if (e.key !== 'Enter' || el.tagName === 'TEXTAREA') return;
    if (el.tagName === 'INPUT' || el.classList.contains('chip')) {
      e.preventDefault();
      void submit();
    }
  };
  /** Chips da linha rápida não tiram o foco do campo de título. */
  const keepFocus = (e: { preventDefault(): void }) => e.preventDefault();

  // com escolhas guardadas da tarefa anterior, a linha rápida continua à vista (ex.: "Hoje" marcado)
  const showChips = expanded || fields.title.length > 0 || fields.date !== null || fields.estimate !== null;
  const isToday = fields.date === today;

  return (
    <form
      ref={formRef}
      className={cls('new-task', active && 'new-task--active', expanded && 'new-task--open', fields.title.length > 0 && 'new-task--filled', disabled && 'is-disabled')}
      onFocus={() => setActive(true)}
      onBlur={onBlur}
      onKeyDown={onFormKey}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="new-task__row">
        <Icon name="plus" size={16} className="new-task__plus" />
        <input
          ref={inputRef}
          className="new-task__input"
          aria-label={t('newTask.label')}
          placeholder={t('newTask.placeholder')}
          value={fields.title}
          disabled={disabled}
          onChange={(e) => setFields({ ...fields, title: e.target.value })}
        />
      </div>
      {errors.title && <p className="field-error">{t(`errors.task.${errors.title}`, { max: LIMITS.title })}</p>}

      {showChips && !expanded && (
        <div className="new-task__quick">
          <button
            type="button"
            className={cls('chip', isToday && 'chip--on')}
            aria-pressed={isToday}
            disabled={disabled}
            onMouseDown={keepFocus}
            onClick={() => setFields({ ...fields, date: isToday ? null : today, time: '' })}
          >
            <Icon name="calendar" size={13} />
            {t('newTask.today')}
          </button>
          {fields.estimate != null && fields.estimate > 0 && (
            <span className="chip chip--static">
              <Icon name="timer" size={13} />
              {formatMinutes(fields.estimate, t)}
            </span>
          )}
          <button type="button" className="link-btn" disabled={disabled} onMouseDown={keepFocus} onClick={() => setExpanded(true)}>
            {t('newTask.moreDetails')}
          </button>
          <span className="new-task__hint">{t('newTask.enterHint')}</span>
        </div>
      )}

      {expanded && (
        <>
          <TaskDetailsEditor value={fields} onChange={setFields} errors={errors} disabled={disabled} />
          <div className="new-task__footer">
            <span className="new-task__hint">{t('newTask.footerHint')}</span>
            <button type="button" className="btn btn--ghost" onClick={exit}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={disabled}>
              <Icon name="check" size={14} />
              {t('common.save')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
